import {FormEvent, useState} from "react";
import {Alert, Badge, Button, Form, Modal, Table} from "react-bootstrap";
import {UsersApi} from "../../api/UsersApi";
import {AccountState, SiteAdministratorClaim, UserViewModel} from "../../api/models";
import {useAuth} from "../../auth/AuthContext";
import {useAsync} from "../../components/useAsync";
import {ErrorAlert} from "../../components/ErrorAlert";
import {LoadingSpinner} from "../../components/LoadingSpinner";
import {PageHeader} from "../../components/PageHeader";
import {ConfirmModal} from "../../components/ConfirmModal";
import {SubmitButton} from "../../components/SubmitButton";
import {emailProblem, formatDate} from "../../components/format";

const isAdmin = (user: UserViewModel) => user.claims?.includes(SiteAdministratorClaim);

function withAdminClaim(claims: string[], admin: boolean): string[] {
    const others = (claims ?? []).filter(x => x !== SiteAdministratorClaim);
    return admin ? [...others, SiteAdministratorClaim] : others;
}

type Confirm = { kind: 'reset' | 'delete', user: UserViewModel };

export function UsersPage() {
    const {user: me} = useAuth();
    const {data: users, error, loading, reload} = useAsync(
        () => UsersApi.getAll().then(x => x.sort((l, r) => l.username.localeCompare(r.username))), []);
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState<UserViewModel>();
    const [confirm, setConfirm] = useState<Confirm>();
    const [busy, setBusy] = useState(false);
    const [actionError, setActionError] = useState<unknown>();
    const [notice, setNotice] = useState<string>();

    // The API refuses to delete the signed-in user or 'admin'.
    const canDelete = (user: UserViewModel) =>
        user.username.toLowerCase() !== 'admin' && user.username.toLowerCase() !== me?.username.toLowerCase();

    const runConfirmed = async () => {
        if (!confirm) {
            return;
        }
        setBusy(true);
        setActionError(undefined);
        try {
            if (confirm.kind === 'reset') {
                await UsersApi.resetPassword(confirm.user.username);
                setNotice(`A new password has been emailed to ${confirm.user.emailAddress}.`);
            } else {
                await UsersApi.remove(confirm.user.username);
                setNotice(`${confirm.user.username} has been deleted.`);
            }
            reload();
        } catch (err) {
            setActionError(err);
        } finally {
            setBusy(false);
            setConfirm(undefined);
        }
    };

    return (
        <>
            <PageHeader title="Users">
                <Button onClick={() => setCreating(true)}><i className="bi bi-person-plus me-1"></i>New user</Button>
            </PageHeader>
            {notice && <Alert variant="success" dismissible onClose={() => setNotice(undefined)}>{notice}</Alert>}
            <ErrorAlert error={error}/>
            <ErrorAlert error={actionError} onClose={() => setActionError(undefined)}/>
            {loading && !users && <LoadingSpinner/>}
            {users && (
                <Table responsive hover>
                    <thead>
                    <tr><th>Username</th><th>Display name</th><th>Email</th><th>Status</th><th>Created</th><th></th></tr>
                    </thead>
                    <tbody>
                    {users.map(user => (
                        <tr key={user.username}>
                            <td>{user.username} {isAdmin(user) && <Badge bg="primary" className="ms-1">Admin</Badge>}</td>
                            <td>{user.displayName}</td>
                            <td>{user.emailAddress}</td>
                            <td>{user.status === AccountState.Enabled ? <Badge bg="success">Enabled</Badge> : <Badge bg="secondary">Disabled</Badge>}</td>
                            <td>{formatDate(user.createdOn)}</td>
                            <td className="text-end text-nowrap">
                                <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => setEditing(user)}>Edit</Button>
                                <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => setConfirm({ kind: 'reset', user })}>Reset password</Button>
                                <Button size="sm" variant="outline-danger" disabled={!canDelete(user)} onClick={() => setConfirm({ kind: 'delete', user })}>Delete</Button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </Table>
            )}
            <CreateUserModal show={creating} onHide={() => setCreating(false)} onCreated={user => {
                setCreating(false);
                setNotice(`${user.username} has been created. Their password has been emailed to ${user.emailAddress}.`);
                reload();
            }}/>
            {editing && <EditUserModal user={editing} isSelf={editing.username.toLowerCase() === me?.username.toLowerCase()}
                                       onHide={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); reload(); }}/>}
            <ConfirmModal show={confirm?.kind === 'reset'} title="Reset password?" confirmLabel="Reset password" variant="warning" busy={busy}
                          onConfirm={runConfirmed} onCancel={() => setConfirm(undefined)}>
                A new password will be generated and emailed to <strong>{confirm?.user.emailAddress}</strong>. Their current
                password stops working, and a disabled account is enabled again.
            </ConfirmModal>
            <ConfirmModal show={confirm?.kind === 'delete'} title="Delete user?" confirmLabel="Delete" busy={busy}
                          onConfirm={runConfirmed} onCancel={() => setConfirm(undefined)}>
                <strong>{confirm?.user.username}</strong> will be deleted. This can't be undone. Licences they own keep working,
                but no longer appear on anyone's account.
            </ConfirmModal>
        </>
    );
}

function CreateUserModal({show, onHide, onCreated}: { show: boolean, onHide(): void, onCreated(user: UserViewModel): void }) {
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [admin, setAdmin] = useState(false);
    const [validated, setValidated] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();
    const problem = emailProblem(email);

    const reset = () => {
        setUsername('');
        setEmail('');
        setDisplayName('');
        setAdmin(false);
        setValidated(false);
        setError(undefined);
    };

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setValidated(true);
        if (problem || !username.trim() || !displayName.trim()) {
            return;
        }
        setBusy(true);
        setError(undefined);
        try {
            const user = await UsersApi.create({
                username: username.trim(),
                emailAddress: email.trim(),
                displayName: displayName.trim(),
                claims: admin ? [SiteAdministratorClaim] : [],
                avatarUrl: '',
                timezone: '',
                language: 'en-US'
            });
            reset();
            onCreated(user);
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal show={show} onHide={() => { reset(); onHide(); }} centered>
            <Form noValidate onSubmit={submit}>
                <Modal.Header closeButton><Modal.Title>New user</Modal.Title></Modal.Header>
                <Modal.Body>
                    <ErrorAlert error={error} messages={{ 409: 'That username or email address is already in use.' }}/>
                    <Form.Group className="mb-3" controlId="new-user-username">
                        <Form.Label>Username</Form.Label>
                        <Form.Control autoFocus value={username} onChange={e => setUsername(e.target.value)} isInvalid={validated && !username.trim()}/>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="new-user-email">
                        <Form.Label>Email address</Form.Label>
                        <Form.Control type="email" value={email} onChange={e => setEmail(e.target.value)} isInvalid={validated && !!problem}/>
                        <Form.Control.Feedback type="invalid">{problem}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="new-user-display-name">
                        <Form.Label>Display name</Form.Label>
                        <Form.Control value={displayName} onChange={e => setDisplayName(e.target.value)} isInvalid={validated && !displayName.trim()}/>
                    </Form.Group>
                    <Form.Check id="new-user-admin" label="Site administrator" checked={admin} onChange={e => setAdmin(e.target.checked)}/>
                    <Form.Text>A password is generated and emailed to the new user.</Form.Text>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { reset(); onHide(); }} disabled={busy}>Cancel</Button>
                    <SubmitButton busy={busy}>Create user</SubmitButton>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}

function EditUserModal({user, isSelf, onHide, onSaved}: { user: UserViewModel, isSelf: boolean, onHide(): void, onSaved(): void }) {
    const [displayName, setDisplayName] = useState(user.displayName);
    const [enabled, setEnabled] = useState(user.status === AccountState.Enabled);
    const [admin, setAdmin] = useState(isAdmin(user));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setError(undefined);
        try {
            // The API replaces every field: keep the ones this form doesn't edit, and any other claims.
            await UsersApi.update(user.username, {
                displayName: displayName.trim(),
                avatarUrl: user.avatarUrl,
                timezone: user.timezone,
                language: user.language,
                defaultProjectId: user.defaultProjectId,
                status: enabled ? AccountState.Enabled : AccountState.Disabled,
                claims: withAdminClaim(user.claims, admin)
            });
            onSaved();
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal show onHide={onHide} centered>
            <Form onSubmit={submit}>
                <Modal.Header closeButton><Modal.Title>Edit {user.username}</Modal.Title></Modal.Header>
                <Modal.Body>
                    <ErrorAlert error={error}/>
                    <Form.Group className="mb-3" controlId="edit-user-email">
                        <Form.Label>Email address</Form.Label>
                        <Form.Control value={user.emailAddress} readOnly plaintext/>
                        <Form.Text>Users change their own address from their profile.</Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="edit-user-display-name">
                        <Form.Label>Display name</Form.Label>
                        <Form.Control required value={displayName} onChange={e => setDisplayName(e.target.value)}/>
                    </Form.Group>
                    {/* Locking yourself out of the admin area from here would need another admin to undo. */}
                    <Form.Check type="switch" id="edit-user-enabled" className="mb-2" label="Account enabled" checked={enabled}
                                disabled={isSelf} onChange={e => setEnabled(e.target.checked)}/>
                    <Form.Check type="switch" id="edit-user-admin" label="Site administrator" checked={admin}
                                disabled={isSelf} onChange={e => setAdmin(e.target.checked)}/>
                    {isSelf && <Form.Text>You can't disable your own account or remove your own administrator access.</Form.Text>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={busy}>Cancel</Button>
                    <SubmitButton busy={busy}>Save</SubmitButton>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}
